import React from 'react';
import { ChevronDown, ExternalLink } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  getFileMeta,
  getPathParts,
} from '@/Modules/Core/Components/FileChangeFeed/fileChangeFeedUtils';

const FileChangeItemCard = ({
  filePath,
  fileHref,
  summaryMetaItems = [],
  actionMeta,
  isExpanded = false,
  isLastVisibleRow = false,
  onToggle,
  details,
  actions,
}) => {
  const fileMeta = getFileMeta(filePath);
  const pathParts = getPathParts(filePath);
  const Icon = fileMeta.icon;

  return (
    <div
      className={cn(
        'bg-transparent',
        !isLastVisibleRow && 'border-b border-border/20'
      )}
    >
      <div className="group relative pl-[3px]">
        <span
          className={cn(
            'absolute inset-y-0 left-0 w-[3px] bg-orange-500 transition-opacity',
            isExpanded ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          )}
        />

        <div
          role="button"
          tabIndex={0}
          className="flex w-full cursor-pointer appearance-none flex-col gap-4 rounded-r-xl rounded-l-none border-0 bg-transparent px-4 py-4 text-left shadow-none outline-none transition-colors hover:bg-primary/3 md:flex-row md:items-start md:justify-between"
          onClick={onToggle}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onToggle();
            }
          }}
        >
          <div className="flex min-w-0 flex-1 items-start gap-4">
            <div
              className={cn(
                'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
                fileMeta.iconClassName
              )}
            >
              <Icon className="h-5 w-5" />
            </div>

            <div className="min-w-0 space-y-2">
              {fileHref ? (
                <a
                  href={fileHref}
                  target="_blank"
                  rel="noreferrer"
                  className="block min-w-0 cursor-pointer text-inherit no-underline hover:text-primary"
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="flex min-w-0 items-center gap-1.5 text-[16px] font-semibold text-foreground transition-colors">
                    <span className="truncate">{pathParts.fileName}</span>
                    <ExternalLink className="h-3.5 w-3.5 shrink-0 text-foreground/60 transition-colors dark:text-muted-foreground" />
                  </div>
                  <div
                    className="truncate text-[13px] text-foreground/72 transition-colors dark:text-muted-foreground"
                    title={pathParts.directory}
                  >
                    {pathParts.directory}
                  </div>
                </a>
              ) : (
                <>
                  <div className="truncate text-[16px] font-semibold text-foreground">
                    {pathParts.fileName}
                  </div>
                  <div
                    className="truncate text-[13px] text-foreground/72 dark:text-muted-foreground"
                    title={pathParts.directory}
                  >
                    {pathParts.directory}
                  </div>
                </>
              )}
              <div className="flex flex-wrap gap-x-4 gap-y-2 text-[12px] text-foreground/72 dark:text-muted-foreground">
                {summaryMetaItems.map((item, index) => {
                  const MetaIcon = item.icon;

                  return (
                    <span
                      key={`${String(item.label)}-${index}`}
                      className="inline-flex items-center gap-2"
                    >
                      {MetaIcon ? <MetaIcon className="h-3.5 w-3.5" /> : null}
                      {item.label}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-3 md:pl-4">
            <span className="inline-flex rounded-full border border-border/40 bg-transparent px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-foreground/70 dark:text-muted-foreground">
              {fileMeta.extensionLabel}
            </span>
            <span
              className={cn(
                'inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]',
                actionMeta.className
              )}
            >
              {actionMeta.label}
            </span>
            <ChevronDown
              className={cn(
                'h-4 w-4 text-foreground/60 transition-transform dark:text-muted-foreground',
                isExpanded && 'rotate-180 text-primary'
              )}
            />
          </div>
        </div>
      </div>

      {isExpanded ? (
        <div className="space-y-4 border-t border-border/20 px-4 py-4">
          {details}
          {actions ? <div className="flex flex-wrap gap-3">{actions}</div> : null}
        </div>
      ) : null}
    </div>
  );
};

export default FileChangeItemCard;
